import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Search, User, FileText, Calendar, Download, Edit, Eye, Trash2, Gift, ArrowRight, ExternalLink, UserPlus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { practiceApi } from '../services/authApi';
import LoadingSpinner from '../components/LoadingSpinner';
import DeletePatientDialog from '../components/DeletePatientDialog';
import { useToast } from '../hooks/use-toast';
import { AskReviewButton } from '../components/reviews/AskReviewButton';
import { useOffices, officeName } from '../components/offices/officesApi';
import { OfficeSelect } from '../components/offices/OfficeSelect';
import { PatientOfficeMover } from '../components/offices/PatientOfficeMover';

const PatientManagementPage = () => {
  const navigate = useNavigate();
  const { practice, user } = useAuth();
  const { open: offices, multi: multiOffice } = useOffices();
  const isAdmin = user?.role !== 'practice_staff';
  const [officeFilter, setOfficeFilter] = useState('');
  const primaryOfficeId = offices.find((o) => o.isPrimary)?.id;
  const patientOffice = (p) => (p.officeId && offices.some((o) => o.id === p.officeId) ? p.officeId : primaryOfficeId);
  const { toast } = useToast();
  
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientProcedures, setPatientProcedures] = useState([]);
  const [patientReferralInfo, setPatientReferralInfo] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingProcedures, setLoadingProcedures] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState(null);

  useEffect(() => {
    loadPatients();
  }, []);

  const loadPatients = async () => {
    try {
      setLoading(true);
      const response = await practiceApi.getPatients();
      
      // Get all patients (removed Gmail restriction)
      const allPatients = response.data || [];
      
      // Add status information for display
      const allPatientsWithStatus = allPatients.map(patient => ({
        ...patient,
        status: patient.isActive !== false ? 'Active' : 'Inactive'
      }));
      
      setPatients(allPatientsWithStatus);
      
    } catch (error) {
      console.error('Load patients error:', error);
      toast({
        title: "Error",
        description: "Failed to load patients",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadPatientProcedures = async (patientId) => {
    try {
      setLoadingProcedures(true);
      
      // Get all procedures for this specific patient
      const response = await practiceApi.getExportData();
      const allData = response.data;
      
      // Find the specific patient and their procedures
      const patient = allData.patients.find(p => p.id === patientId);
      if (patient && patient.assignedProcedures) {
        setPatientProcedures(patient.assignedProcedures);
      } else {
        setPatientProcedures([]);
      }
      
    } catch (error) {
      console.error('Load patient procedures error:', error);
      toast({
        title: "Error",
        description: "Failed to load patient procedures",
        variant: "destructive",
      });
    } finally {
      setLoadingProcedures(false);
    }
  };

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    loadPatientProcedures(patient.id);
    loadPatientReferralInfo(patient);
  };

  const loadPatientReferralInfo = async (patient) => {
    try {
      const token = localStorage.getItem('dentalToken');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const referralInfo = {
        referredFrom: null,
        referredTo: []
      };
      
      // Check if patient has inbound referral source
      if (patient.inboundReferralSourceId) {
        try {
          const sourceResponse = await fetch(`${backendUrl}/api/inbound-referrals/sources/${patient.inboundReferralSourceId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (sourceResponse.ok) {
            const sourceData = await sourceResponse.json();
            referralInfo.referredFrom = sourceData.source;
          }
        } catch (err) {
          console.error('Error loading referral source:', err);
        }
      }
      
      // Check for outbound referrals (patient referred to specialists)
      try {
        const referralsResponse = await fetch(`${backendUrl}/api/referrals/patient/${patient.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (referralsResponse.ok) {
          const referralsData = await referralsResponse.json();
          referralInfo.referredTo = referralsData.referrals || [];
        }
      } catch (err) {
        console.error('Error loading outbound referrals:', err);
      }
      
      setPatientReferralInfo(referralInfo);
    } catch (error) {
      console.error('Load referral info error:', error);
      setPatientReferralInfo(null);
    }
  };

  const handleEditPatient = (patient) => {
    // Navigate to edit patient page with patient data
    navigate('/edit-patient', { state: { patient } });
  };

  const handleDeletePatient = (patient) => {
    setPatientToDelete(patient);
    setDeleteDialogOpen(true);
  };

  const confirmDeletePatient = async (hardDelete = false) => {
    if (!patientToDelete) return;

    try {
      await practiceApi.deletePatient(patientToDelete.id, hardDelete);
      
      toast({
        title: "Success!",
        description: hardDelete 
          ? `${patientToDelete.firstName} ${patientToDelete.lastName} has been permanently deleted.`
          : `${patientToDelete.firstName} ${patientToDelete.lastName} has been deactivated.`,
        variant: "default",
      });

      // Refresh the patients list
      await loadPatients();
      
      // Clear selection if the deleted patient was selected
      if (selectedPatient?.id === patientToDelete.id) {
        setSelectedPatient(null);
        setPatientProcedures([]);
      }
      
    } catch (error) {
      console.error('Delete patient error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete patient. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setPatientToDelete(null);
    }
  };

  const handlePrintProcedure = async (procedureId) => {
    try {
      toast({
        title: "Generating PDF",
        description: "Creating branded post-operative care document...",
        variant: "default",
      });
      
      const response = await practiceApi.getProcedureAssignment(procedureId);
      const data = response.data;
      
      // FINAL RAW TEXT ONLY - CACHE BUSTED
      const { generateProcedurePDF } = await import('../utils/ENHANCED_PDF_WITH_LOGO');
      
      console.log('🏥 PDF Generation - Practice Context:', practice);
      console.log('📊 Practice officeHours:', practice?.officeHours);
      console.log('📞 Practice emergencyContact:', practice?.emergencyContact);
      
      const procedureForPDF = {
        id: data.procedure.id,
        name: data.assignment.procedureName,
        specialty: data.procedure.specialty,
        specialtyName: data.procedure.specialtyName,
        overview: data.procedure.overview,
        immediateAftercare: data.procedure.immediateAftercare || [],
        dietRestrictions: data.procedure.dietRestrictions || [],
        warningSignsToCallDoctor: data.procedure.warningSignsToCallDoctor || [],
        recoveryTimeline: data.procedure.recoveryTimeline || [],
        medications: data.procedure.medications || [],
        patientName: data.patient ? `${data.patient.firstName} ${data.patient.lastName}` : 'Patient',
        patientEmail: data.patient ? data.patient.email : '',
        dentistName: data.assignment.dentistName,
        performedDate: new Date(data.assignment.performedDate).toLocaleDateString(),
        followUpDate: data.assignment.followUpDate ? new Date(data.assignment.followUpDate).toLocaleDateString() : null,
        status: data.assignment.status,
        practiceNotes: data.assignment.practiceNotes,
        customInstructions: data.assignment.customInstructions || [],
        practiceName: practice?.name || 'Dental Practice',
        practiceAddress: practice?.address || practice?.location || '',
        practicePhone: practice?.phone || '',
        practiceWebsite: practice?.website || '',
        practiceOfficeHours: practice?.officeHours || '',
        practiceEmergencyContact: practice?.emergencyContact || ''
      };
      
      console.log('📄 Final procedure object for PDF:', {
        practiceName: procedureForPDF.practiceName,
        practiceOfficeHours: procedureForPDF.practiceOfficeHours,
        practiceEmergencyContact: procedureForPDF.practiceEmergencyContact,
        practiceData: practice
      });
      
      const success = generateProcedurePDF(procedureForPDF, practice);
      
      if (success) {
        toast({
          title: "PDF Ready",
          description: `${data.assignment.procedureName} document downloaded successfully.`,
          variant: "default",
        });
      } else {
        throw new Error('PDF generation failed');
      }
      
    } catch (error) {
      console.error('Print error:', error);
      toast({
        title: "Print Failed",
        description: "Failed to generate PDF. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Filter patients based on search term
  const filteredPatients = patients.filter(patient => {
    if (officeFilter && patientOffice(patient) !== officeFilter) return false;
    const searchLower = searchTerm.toLowerCase();
    return [patient.firstName, patient.lastName, patient.email, patient.phone]
      .some((v) => (v || '').toLowerCase().includes(searchLower));
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/')}
              className="flex items-center self-start sm:self-auto"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">Patient Management</h1>
              <p className="text-gray-600">
                Manage patients and their procedure assignments
              </p>
            </div>
            <Button onClick={() => navigate('/add-patient?returnTo=/patient-management')}
              className="bg-emerald-700 hover:bg-emerald-800 text-white shrink-0" data-testid="manage-patients-add-one-btn">
              <UserPlus className="h-4 w-4 mr-2" /> Add One Patient
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Side - Patient List */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <User className="h-5 w-5 mr-2 text-blue-600" />
                  Patients ({filteredPatients.length})
                </CardTitle>
                {/* Search Bar */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search patients..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                {multiOffice && isAdmin && !sessionStorage.getItem('practcomOfficeId') && (
                  <OfficeSelect offices={offices} value={officeFilter} onChange={setOfficeFilter} allLabel="All offices" testId="patients-office-filter" className="w-full mt-2" />
                )}
                {/* Filter Info */}
                <div className="flex items-center justify-between text-sm text-gray-600 mt-2">
                  <span>{officeFilter ? `Showing ${officeName(offices, officeFilter)}` : 'Showing all patients'}</span>
                  <Badge variant="outline" className="text-xs">
                    {filteredPatients.length} patients
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <LoadingSpinner />
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {filteredPatients.map((patient) => (
                      <div
                        key={patient.id}
                        className={`p-3 rounded-lg border transition-colors ${
                          selectedPatient?.id === patient.id
                            ? 'bg-blue-50 border-blue-200'
                            : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div 
                            onClick={() => handlePatientSelect(patient)}
                            className="flex-1 cursor-pointer"
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-medium text-sm">
                                {patient.firstName} {patient.lastName}
                              </p>
                              <Badge 
                                variant="outline" 
                                className="text-xs bg-blue-50 text-blue-600 border-blue-200"
                              >
                                Patient
                              </Badge>
                              <Badge 
                                variant={patient.status === 'Active' ? 'default' : 'secondary'}
                                className={`text-xs ${
                                  patient.status === 'Active' 
                                    ? 'bg-green-100 text-green-800 border-green-200' 
                                    : 'bg-gray-100 text-gray-600 border-gray-200'
                                }`}
                              >
                                {patient.status || 'Active'}
                              </Badge>
                            </div>
                            <p className="text-xs text-gray-500">{patient.email}</p>
                            {multiOffice && <p className="text-xs text-emerald-700" data-testid={`patient-office-${patient.id}`}>{officeName(offices, patient.officeId)}</p>}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditPatient(patient);
                              }}
                              className="h-8 w-8 p-0"
                              title="Edit Patient"
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePatient(patient);
                              }}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Delete Patient"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {filteredPatients.length === 0 && (
                      <p className="text-center text-gray-500 py-4">
                        {searchTerm ? 'No patients found matching your search' : 'No patients found'}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Side - Patient Procedures */}
          <div className="lg:col-span-2">
            {selectedPatient ? (
              <>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-blue-600" />
                    Procedures for {selectedPatient.firstName} {selectedPatient.lastName}
                    <span className="ml-auto flex items-center gap-2">
                      {multiOffice && isAdmin && (
                        <PatientOfficeMover key={selectedPatient.id} patient={selectedPatient} offices={offices} toast={toast}
                          onMoved={(oid) => { setSelectedPatient({ ...selectedPatient, officeId: oid }); setPatients((ps) => ps.map((p) => (p.id === selectedPatient.id ? { ...p, officeId: oid } : p))); }} />
                      )}
                      <AskReviewButton patientId={selectedPatient.id} toast={toast} />
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingProcedures ? (
                    <div className="flex justify-center py-8">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {patientProcedures.length > 0 ? (
                        patientProcedures.map((procedure) => (
                          <div key={procedure.id} className="border rounded-lg p-4 bg-white">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <h3 className="font-semibold text-lg">{procedure.procedureName}</h3>
                                <div className="mt-2 space-y-1">
                                  <p className="text-sm text-gray-600">
                                    <Calendar className="h-4 w-4 inline mr-2" />
                                    Performed: {new Date(procedure.performedDate).toLocaleDateString()}
                                  </p>
                                  <p className="text-sm text-gray-600">
                                    Dentist: {procedure.dentistName}
                                  </p>
                                  {procedure.practiceNotes && (
                                    <p className="text-sm text-gray-600">
                                      Notes: {procedure.practiceNotes}
                                    </p>
                                  )}
                                </div>
                                <Badge variant="outline" className="mt-2">
                                  {procedure.status}
                                </Badge>
                              </div>
                              <div className="flex flex-col space-y-2 ml-4">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => navigate(`/procedure-details/${procedure.id}`)}
                                  className="text-xs"
                                >
                                  <Eye className="h-3 w-3 mr-1" />
                                  View
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => navigate(`/edit-procedure/${procedure.id}`)}
                                  className="text-xs"
                                >
                                  <Edit className="h-3 w-3 mr-1" />
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handlePrintProcedure(procedure.id)}
                                  className="text-xs"
                                >
                                  <Download className="h-3 w-3 mr-1" />
                                  PDF
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8">
                          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-500">
                            No procedures assigned to {selectedPatient.firstName} {selectedPatient.lastName}
                          </p>
                          <Button
                            onClick={() => navigate('/assign-procedure')}
                            className="mt-4"
                          >
                            Assign Procedure
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Referral Tracking Section */}
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Gift className="h-5 w-5 mr-2 text-green-600" />
                    Referral Tracking
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Referral From (Who referred this patient to the practice) - PURPLE/VIOLET theme */}
                    <div className="border-2 border-purple-200 rounded-lg p-4 bg-purple-50/30" data-testid="referral-from-section">
                      <h4 className="font-semibold text-sm text-purple-800 mb-3 flex items-center">
                        <ArrowRight className="h-4 w-4 mr-2 text-purple-600" />
                        Referred FROM (Source)
                        <Badge className="ml-2 bg-purple-100 text-purple-700 text-xs">Inbound</Badge>
                      </h4>
                      {patientReferralInfo?.referredFrom ? (
                        <div className="bg-purple-100 rounded-lg p-3 border border-purple-200">
                          <p className="font-medium text-purple-900">{patientReferralInfo.referredFrom.name}</p>
                          <Badge variant="outline" className="mt-1 text-xs bg-purple-50 text-purple-700 border-purple-300">
                            {patientReferralInfo.referredFrom.sourceType || 'Unknown'}
                          </Badge>
                          {patientReferralInfo.referredFrom.email && (
                            <p className="text-xs text-purple-700 mt-2">{patientReferralInfo.referredFrom.email}</p>
                          )}
                          {patientReferralInfo.referredFrom.phone && (
                            <p className="text-xs text-purple-700">{patientReferralInfo.referredFrom.phone}</p>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate('/referral-management?tab=from')}
                            className="mt-2 text-xs border-purple-300 text-purple-700 hover:bg-purple-100"
                          >
                            <ExternalLink className="h-3 w-3 mr-1" />
                            View in Referral Management
                          </Button>
                        </div>
                      ) : (
                        <div className="text-center py-4 bg-purple-50 rounded-lg border border-purple-100">
                          <p className="text-sm text-purple-600">No referral source linked</p>
                          <p className="text-xs text-purple-400 mt-1">Patient was not referred by another source</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Referral To (Where this patient was referred to) - GREEN/TEAL theme */}
                    <div className="border-2 border-teal-200 rounded-lg p-4 bg-teal-50/30" data-testid="referral-to-section">
                      <h4 className="font-semibold text-sm text-teal-800 mb-3 flex items-center">
                        <ArrowRight className="h-4 w-4 mr-2 text-teal-600 rotate-180" />
                        Referred TO (Specialist)
                        <Badge className="ml-2 bg-teal-100 text-teal-700 text-xs">Outbound</Badge>
                      </h4>
                      {patientReferralInfo?.referredTo?.length > 0 ? (
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {patientReferralInfo.referredTo.map((referral, idx) => (
                            <div key={referral.id || idx} className="bg-teal-100 rounded-lg p-3 border border-teal-200">
                              <p className="font-medium text-sm text-teal-900">{referral.sourceName || referral.referralSourceName}</p>
                              <Badge variant="outline" className="mt-1 text-xs bg-teal-50 text-teal-700 border-teal-300">
                                {referral.sourceSpecialty || referral.specialty}
                              </Badge>
                              <p className="text-xs text-teal-700 mt-1">
                                {referral.createdAt ? new Date(referral.createdAt).toLocaleDateString() : 'N/A'}
                              </p>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/view-referral/${referral.id}`)}
                                className="mt-2 text-xs border-teal-300 text-teal-700 hover:bg-teal-100"
                              >
                                <Eye className="h-3 w-3 mr-1" />
                                View Referral
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4 bg-teal-50 rounded-lg border border-teal-100">
                          <p className="text-sm text-teal-600">No outbound referrals</p>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate('/referral-management')}
                            className="mt-2 text-xs border-teal-300 text-teal-700 hover:bg-teal-100"
                          >
                            Create Referral
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
              </>
            ) : (
              <Card>
                <CardContent className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">
                      Select a patient from the list to view their procedures
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Delete Patient Dialog */}
      <DeletePatientDialog
        patient={patientToDelete}
        isOpen={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setPatientToDelete(null);
        }}
        onConfirm={confirmDeletePatient}
        activeProcedures={patientToDelete?.procedureCount || 0}
      />
    </div>
  );
};

export default PatientManagementPage;